import { LicencedIntervenor } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicencedIntervenor";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { PatientRefIntervenor, PatientRefIntervenorWithDetails } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { SpecialisationCategory, TitleAndSpecialtyModel } from "@/Common/Models/MedfarDictionaries/TitleAndSpecialtyModel";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { DirectorySearchHelper } from "@/ContactDirectory/Helpers/DirectorySearchHelper";
import { PhoneNumberHelper } from "@/ContactDirectory/Helpers/PhoneNumberHelper";
import { DirectoryAddress } from "@/ContactDirectory/Models/DirectoryAddress";
import { DirectoryFaxNumber } from "@/ContactDirectory/Models/DirectoryFaxNumber";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";

class PatientRefIntervenorWithDetailsModel {
    private SpecialisationCategory: SpecialisationCategory; // Use GetCategory;
    private TitleAndSpecialtyModel: TitleAndSpecialtyModel; // Use GetSpeciality

    public Model: PatientRefIntervenorWithDetails;
    public IsEditMode: boolean;
    public Employee: EmployeeUIModel;
    public EmployeeSpecialityDetails: string;

    public SearchByEmployee: boolean;

    constructor(model: PatientRefIntervenorWithDetails, isEditMode?: boolean, searchByEmployee?: boolean) {
        this.Model = model;
        this.IsEditMode = isEditMode ?? false;
        this.SearchByEmployee = searchByEmployee ?? false;
        this.InitEmployee();
    }

    public GetNewIntervenorDTO(): PatientRefIntervenor {
        var tmp = this.Model.Intervenor;
        tmp.LicenceOrganisationRuleId = this.Model.LicenceOrganisationRule != null ? this.Model.LicenceOrganisationRule.Id : null;
        tmp.LicenceOrganisationSpecialisationId = this.Model.LicenceOrganisationSpecialisation != null ? this.Model.LicenceOrganisationSpecialisation.Id : null;
        tmp.IntervenorId = this.Model.LicencedIntervenor ? this.Model.LicencedIntervenor.Id : null;
        tmp.HealthCareProviderId = this.Model.HealthCareProvider?.id;
        tmp.DirectoryFaxNumberId = this.Model.Intervenor.DirectoryFaxNumberId;
        return tmp;
    }

    public InitEmployee(): void {
        if (this.Model.Intervenor.EmployeeId == null)
            return;

        var employeeMatches = cacheStore.EmployeeCacheWithInactive.filter(x => x.ContactFacet_id == this.Model.Intervenor.EmployeeId);
        if (employeeMatches.length > 0) {
            this.Employee = EmployeeUIModel.fromCompositeObject(employeeMatches[0]);
            this.SearchByEmployee = true;
        }

        // AutoSelect Speciality
        if (this.Model.LicenceOrganisationRule == null) {
            const specialties = this.initSearchableEmployeeSpecialties();

            if (specialties.length == 1)
                this.FromSpecialityResult(specialties[0].Model);
        }
    }

    public Clone(): PatientRefIntervenorWithDetailsModel {
        return new PatientRefIntervenorWithDetailsModel(JSON.parse(JSON.stringify(this.Model)));
    }

    public static New(IsEmployee: boolean = false, IsEditMode: boolean = true): PatientRefIntervenorWithDetailsModel {
        var model = new PatientRefIntervenorWithDetails();
        model.Intervenor = new PatientRefIntervenor();

        var NewPatientRefIntervenorWithDetailsModel = new PatientRefIntervenorWithDetailsModel(model);
        NewPatientRefIntervenorWithDetailsModel.IsEditMode = IsEditMode;
        NewPatientRefIntervenorWithDetailsModel.SearchByEmployee = IsEmployee;

        return NewPatientRefIntervenorWithDetailsModel;
    }

    public initSearchableEmployeeSpecialties(): SearchableItemModelT<TitleAndSpecialtyModel>[] {
        if (!this.Employee)
            return [];

        // Get a default string Value for Speciality
        const specializations = this.Employee.getServiceSpecializations();

        let speciality = "";
        if (specializations != null && specializations.length > 0)
            speciality = specializations.map(p => p.GetValue()).join(",");

        this.EmployeeSpecialityDetails = speciality; // Speciality Raw Text to help selecting the right specialisation

        // Autoselect if only 1 is available
        let specialties = cacheStore.TitlesAndSpecialtiesCache.toArray().filterSearchAdvanced(speciality, (IsMatchingSearchTextFct, x: SearchableItemModelT<TitleAndSpecialtyModel>) => IsMatchingSearchTextFct(x.Value_Searchable));

        if (specialties.length > 1)
            specialties = specialties.filter(p => p.Model.licenceOrganisationRuleModel.Title !== EmployeeTitle.Resident); // remove resident from Autoselect

        return specialties;
    }

    public FromSpecialityResult(Speciality: TitleAndSpecialtyModel): this {
        this.TitleAndSpecialtyModel = null; // Clear Speciality Cached Model
        this.Model.LicenceOrganisationRule = Speciality.licenceOrganisationRuleModel;
        this.Model.LicenceOrganisationSpecialisation = Speciality.licenceOrganisationSpecialisationModel;

        return this;
    }

    public DeleteSpeciality(): void {
        this.SpecialisationCategory = null; // Clear Speciality Cached
        this.TitleAndSpecialtyModel = null; // Clear Speciality Cached Model
        this.Model.LicenceOrganisationRule = null;
        this.Model.LicenceOrganisationSpecialisation = null;
    }

    public DeleteIntervenorSelection(shouldKeepIntervenorId?: boolean, shouldKeepNote?: boolean): void {
        this.Model.Intervenor = new PatientRefIntervenor(
            shouldKeepIntervenorId ? this.Model.Intervenor.Id : null,
            shouldKeepNote ? this.Model.Intervenor.Note : ""
        );
        this.Model.HealthCareProvider = null;
        this.Model.LicencedIntervenor = null;
        this.Employee = null; // Clear EmployeeCache
        this.DeleteSpeciality();
    }

    public getTitleAndSpecialtyModel(): TitleAndSpecialtyModel {
        if (this.TitleAndSpecialtyModel == null || this.isSpecialtyModelUpdated())
            this.TitleAndSpecialtyModel = new TitleAndSpecialtyModel(this.Model.LicenceOrganisationRule, this.Model.LicenceOrganisationSpecialisation);

        return this.TitleAndSpecialtyModel;
    }

    public GetSpecialisationCategory(): SpecialisationCategory {
        if (this.SpecialisationCategory == null || (!!this.TitleAndSpecialtyModel && this.isSpecialtyModelUpdated()))
            this.SpecialisationCategory = TitleAndSpecialtyModel.getCategory(this.getTitleAndSpecialtyModel());

        return this.SpecialisationCategory;
    }

    public GetSpecialisationCategoryName(): string {
        return this.GetSpecialisationCategory().Name;
    }

    public updateTitleAndSpecialtyFromLicencedintervenor(licencedIntervenor: LicencedIntervenor): void {
        if (!licencedIntervenor)
            return;

        this.Model.LicencedIntervenor = licencedIntervenor;

        // Auto Select Speciality
        if (this.Model.LicenceOrganisationRule == null && licencedIntervenor.LicenceOrganisationRuleId != null) {
            var specialities = cacheStore.TitlesAndSpecialtiesCache.filter(x => x.Model.licenceOrganisationRuleModel.Id == licencedIntervenor.LicenceOrganisationRuleId);

            if (licencedIntervenor.LicenceOrganisationSpecialisationId != null)
                specialities = specialities.filter(p => p.Model.licenceOrganisationSpecialisationModel.Id == licencedIntervenor.LicenceOrganisationSpecialisationId);

            if (specialities.count() > 1)
                specialities = specialities.filter(p => p.Model.licenceOrganisationRuleModel.Title !== EmployeeTitle.Resident); // remove resident from Autoselect

            if (specialities.count() == 1)
                this.FromSpecialityResult(specialities.get(0).Model);
        }
    }

    public static FromLicencedintervenor(licencedIntervenor: LicencedIntervenor): PatientRefIntervenorWithDetailsModel {
        if (licencedIntervenor == null)
            return null;

        var NewRefIntervenor = PatientRefIntervenorWithDetailsModel.New();
        NewRefIntervenor.updateTitleAndSpecialtyFromLicencedintervenor(licencedIntervenor);

        return NewRefIntervenor;
    }

    public updateTitleAndSpecialtyFromHealthCareProvider(healthCareProvider: DirectoryHealthCareProvider): void {
        this.Model.HealthCareProvider = healthCareProvider;

        // Auto Select Speciality
        if (healthCareProvider.licenseOrganizationRuleId) {
            let specialties = cacheStore.TitlesAndSpecialtiesCache.filter(x => x.Model.licenceOrganisationRuleModel.Id == healthCareProvider.licenseOrganizationRuleId);

            if (healthCareProvider.licenseOrganizationSpecializationId != null)
                specialties = specialties.filter(x => x.Model.licenceOrganisationSpecialisationModel.Id == healthCareProvider.licenseOrganizationSpecializationId);

            if (specialties.count() > 1)
                specialties = specialties.filter(x => x.Model.licenceOrganisationRuleModel.Title !== EmployeeTitle.Resident); // remove resident from Autoselect

            if (specialties.count() == 1)
                this.FromSpecialityResult(specialties.get(0).Model);
        }
    }

    public IsFromClinic(): boolean {
        return this.Employee != null;
    }

    public GetDoctorId(): string {
        if (cacheStore.ClinicSettings.EnableContactDirectory && !IsNullOrUndefined(this.Model.HealthCareProvider?.id))
            return this.Model.HealthCareProvider.id.toString();
        else if (this.Model.LicencedIntervenor && !IsNullOrWhiteSpace(this.Model.LicencedIntervenor.Id))
            return this.Model.LicencedIntervenor.Id;
        else if (this.IsFromClinic())
            return this.Model.Intervenor.EmployeeId;
        else
            return "";
    }

    public GetName(): string {
        if (cacheStore.ClinicSettings.EnableContactDirectory && this.Model.HealthCareProvider)
            return `${this.Model.HealthCareProvider?.lastName}, ${this.Model.HealthCareProvider?.firstName}`;
        else if (this.Model.LicencedIntervenor)
            return this.Model.LicencedIntervenor?.Name
                ?? new LicencedIntervenor(this.Model.LicencedIntervenor)?.Name
                ?? "";
        else if (this.IsFromClinic())
            return this.Employee.nameWithPrefix();
        else
            return "";
    }

    public GetNameSearch(): string {
        if (this.Model.LicencedIntervenor)
            return this.Model.LicencedIntervenor.LastName + " " + this.Model.LicencedIntervenor.FirstName;

        else if (this.IsFromClinic()) {
            return this.Employee.Name;
        }
        else
            return "";
    }

    public GetAddress(): string {
        if (cacheStore.ClinicSettings.EnableContactDirectory && this.Model.HealthCareProvider) {
            const establishmentWithSelectedFaxNumber = this.Model.HealthCareProvider
                ?.linkedEstablishments
                .firstOrDefault(p =>
                    p.faxNumbers?.some(fax =>
                        fax.id === this.Model.Intervenor?.DirectoryFaxNumberId
                    ));

            return (this.Model.HealthCareProvider?.rawAddress
                || DirectorySearchHelper.formatAddress(DirectoryAddress.fromSearchDto(establishmentWithSelectedFaxNumber?.address))
            ) ?? "";
        }
        else if (this.Model.LicencedIntervenor && this.Model.LicencedIntervenor.Address != null) {
            return this.Model.LicencedIntervenor.Address.replace(/(?:\r\n|\r|\n)/g, " ");
        }
        else
            return "";
    }

    public GetLicence(AddParenthesis: boolean = false): string {
        let value = "";

        if (cacheStore.ClinicSettings.EnableContactDirectory && this.Model.HealthCareProvider)
            value = this.Model.HealthCareProvider?.licenseNumber;
        else if (this.Model.LicencedIntervenor && !IsNullOrWhiteSpace(this.Model.LicencedIntervenor.LicenceNumber))
            value = LicencedIntervenor.GetLicence(this.Model.LicencedIntervenor);
        else if (this.IsFromClinic() && !IsNullOrWhiteSpace(this.Employee.Model.FirstLicenceNumber))
            value = this.Employee.Model.FirstLicenceNumber;
        else
            value = "";

        if (AddParenthesis && !IsNullOrWhiteSpace(value))
            value = "(" + value + ")";

        return value;
    }

    public IsValid(): boolean {
        if (cacheStore.ClinicSettings.EnableContactDirectory && this.Model.HealthCareProvider)
            return !IsNullOrWhiteSpace(this.Model.HealthCareProvider.licenseOrganizationRuleId);
        else if (!this.IsFromClinic())
            return this.Model.LicencedIntervenor && TitleAndSpecialtyModel.isValid(this.getTitleAndSpecialtyModel()) == true;
        else
            return this.Model.Intervenor.EmployeeId != null && TitleAndSpecialtyModel.isValid(this.getTitleAndSpecialtyModel()) == true;
    }

    public GetOrganizationAcronym(): string {
        return !IsNullOrWhiteSpace(this.Model.LicenceOrganisationRule?.Acronym)
            ? this.Model.LicenceOrganisationRule.Acronym
            : "";
    }

    public GetPhone(replaceEmptyString: boolean = true): string {
        if (cacheStore.ClinicSettings.EnableContactDirectory && this.Model.HealthCareProvider) {
            return DirectoryHealthCareProvider
                .fromSearchDto(this.Model.HealthCareProvider)
                .getPhoneNumbersForDisplay(true)
                ?.firstOrDefault()
                ?? "";
        }
        else {
            return replaceEmptyString
                ? MyleHelper.ReplaceEmptyString(this.Model.LicencedIntervenor?.Phone)
                : this.Model.LicencedIntervenor?.Phone;
        }
    }

    public GetFax(replaceEmptyString: boolean = true): string {
        if (cacheStore.ClinicSettings.EnableContactDirectory && this.Model.HealthCareProvider) {
            const linkedEstablishmentsFaxNumbers = this.Model.HealthCareProvider?.linkedEstablishments?.flatMap(p => p.faxNumbers);
            const faxNumbers = [this.Model.HealthCareProvider?.faxNumber, ...linkedEstablishmentsFaxNumbers].filter(p => !!p);

            const selectedFaxNumber = faxNumbers?.firstOrDefault(p => p.id === this.Model.Intervenor?.DirectoryFaxNumberId);
            return PhoneNumberHelper.formatDirectoryNumber(
                DirectoryFaxNumber.fromSearchDto(
                    selectedFaxNumber
                    ?? linkedEstablishmentsFaxNumbers?.firstOrDefault()
                    ?? this.Model.HealthCareProvider.faxNumber
                )
            );
        }
        else {
            return replaceEmptyString
                ? MyleHelper.ReplaceEmptyString(this.Model.LicencedIntervenor?.Fax)
                : this.Model.LicencedIntervenor?.Fax;
        }
    }

    public GetSpecialityForSearch(): string {
        if (this.Model.LicenceOrganisationRule != null || this.Model.LicenceOrganisationSpecialisation != null)
            return TitleAndSpecialtyModel.getName(this.getTitleAndSpecialtyModel());
        else if (this.Model.LicencedIntervenor)
            return this.Model.LicencedIntervenor.SpecialityDetails;
        else if (!IsNullOrWhiteSpace(this.EmployeeSpecialityDetails))
            return this.EmployeeSpecialityDetails;

        return "";
    }

    public getSpecialtyName(): string {
        const specialtyModel = this.getTitleAndSpecialtyModel();
        if (specialtyModel != null)
            return TitleAndSpecialtyModel.getName(specialtyModel);
        else
            return " -- ";
    }

    public GetLicenceOrganisatioRuleName(): string {
        return LicenceOrganisationRule.getTitleName(this.Model.LicenceOrganisationRule);
    }

    public GetOrganisationName(): string {
        const titleAndSpecialtyModel = this.getTitleAndSpecialtyModel();
        if (TitleAndSpecialtyModel.isValid(titleAndSpecialtyModel)) {
            let organisationName = "";
            if (!IsNullOrWhiteSpace(titleAndSpecialtyModel.licenceOrganisationRuleModel.OrganisationName))
                organisationName += titleAndSpecialtyModel.licenceOrganisationRuleModel.OrganisationName;

            if (!IsNullOrWhiteSpace(titleAndSpecialtyModel.licenceOrganisationRuleModel.Acronym))
                organisationName += ` (${titleAndSpecialtyModel.licenceOrganisationRuleModel.Acronym})`;

            return organisationName;
        }
        else
            return "";
    }

    private isSpecialtyModelUpdated(): boolean {
        return this.TitleAndSpecialtyModel.licenceOrganisationRuleModel?.Id !== this.Model.LicenceOrganisationRule?.Id
            || this.TitleAndSpecialtyModel.licenceOrganisationSpecialisationModel?.Id !== this.Model.LicenceOrganisationSpecialisation?.Id;
    }
}

export { PatientRefIntervenorWithDetailsModel };
